import { jsx, jsxs } from "react/jsx-runtime";
import { useFlowState, track, useMutation } from "@betterment/js-runtime";
import { WorkflowHeader, BettermentLogo, MaskedInput, CenteredWorkflowLayout, Text, InputGroup, Label, ErrorText, Button, Box } from "@betterment/style-closet-react";
import { useField, Formik, Form, ErrorMessage } from "formik";
import pick from "lodash/pick";
import * as Yup from "yup";
import { useHistory } from "react-router";
import { e as exitIntentConfirmation } from "./exitIntent-D2_P6WHh.js";
import { u as urlBuilder, c as navigate } from "./index-BEglnXN9.js";
import { F as Footer } from "./Footer-BPSPPtJk.js";
import { R as RedirectToPreviousStep } from "./RedirectToPreviousStep-BwydGlvM.js";
import { S as SecureInfoBadge } from "./SecureInfoBadge-DjTZpTAN.js";
import { w as withoutOutline } from "./WithoutOutline-_MwFlIvo.js";
import { P as PhoneNumberSchema } from "./phone-number-schema-DfpW0Nhe.js";
import { c as convertApiErrorsToFormikMsg, o as overridePhoneNumberErrorMessage } from "./form-helpers-C1M2v4FK.js";
const Logo = () => /* @__PURE__ */ jsx(BettermentLogo, { height: "2.1rem" });
const SignupFlowHeader = () => {
  const { goToPreviousStep, flowState } = useFlowState();
  const history = useHistory();
  const steps = flowState.steps.length - 1;
  const getCurrentStep = () => flowState.steps.findIndex((step) => step === flowState.currentStep) + 1;
  const currentStep = getCurrentStep();
  const brochureUrl = urlBuilder.getBrochureUrl();
  const closeHandler = () => {
    track("ElementClicked", { name: "exitFlow", flow: "SignupFlow" });
    history.push(flowState.basePath);
    navigate(brochureUrl);
  };
  const backButtonHandler = () => {
    track("ElementClicked", { name: "previousStep", flow: "SignupFlow" });
    goToPreviousStep();
  };
  const exitIntent = () => {
    exitIntentConfirmation({ callback: () => closeHandler() });
  };
  return /* @__PURE__ */ jsx(
    WorkflowHeader,
    {
      logo: currentStep === 1 ? Logo : void 0,
      logoHandler: currentStep === 1 ? exitIntent : void 0,
      title: `${currentStep} of ${steps}`,
      backButtonHandler: currentStep === 1 ? void 0 : backButtonHandler,
      closeHandler: exitIntent
    }
  );
};
const FormikMaskedInput = ({
  name,
  type,
  ...rest
}) => {
  const [field] = useField({ name, type });
  return /* @__PURE__ */ jsx(MaskedInput, { ...field, ...rest });
};
const FormikMaskedField = withoutOutline(FormikMaskedInput);
const StepSchema = Yup.object().shape({
  phoneNumber: PhoneNumberSchema
});
const PhoneNumber = ({ flowState, goToNextStep }) => {
  const [validatePhoneNumber] = useMutation(urlBuilder.apiV2ValidatePhoneNumber.absolute());
  if (!flowState.values.legalFirstName || !flowState.values.lastName) {
    return /* @__PURE__ */ jsx(RedirectToPreviousStep, {});
  }
  const validate = async ({ phoneNumber }) => {
    const { errors } = await validatePhoneNumber({ phoneNumber });
    if (errors) {
      return convertApiErrorsToFormikMsg(errors, { overrideErrorMessage: overridePhoneNumberErrorMessage });
    }
  };
  return /* @__PURE__ */ jsxs(
    CenteredWorkflowLayout,
    {
      variant: "five-column",
      title: "Phone Number",
      heading: "Almost there. Next, we need your phone number.",
      children: [
        /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "header", children: /* @__PURE__ */ jsx(SignupFlowHeader, {}) }),
        /* @__PURE__ */ jsxs(CenteredWorkflowLayout.Slot, { children: [
          /* @__PURE__ */ jsx(Text, { as: "p", color: "bmtGrey60", children: `We use this to secure your account with two-factor authentication. We'll never sell your info or spam you. This must be a U.S. phone number. Additionally, by providing your phone number to Betterment, you understand and agree that Betterment may contact you by phone regarding opening and/or funding an account. You may request to be put on a "Do Not Call" list maintained by Betterment.` }),
          /* @__PURE__ */ jsx(
            Formik,
            {
              onSubmit: goToNextStep,
              validationSchema: StepSchema,
              validate,
              initialValues: pick(flowState.values, Object.keys(StepSchema.getDefault())),
              validateOnChange: false,
              validateOnBlur: false,
              children: ({ isValidating }) => /* @__PURE__ */ jsxs(Form, { children: [
                /* @__PURE__ */ jsxs(InputGroup, { fullWidth: true, mb: [2, 8, 9], children: [
                  /* @__PURE__ */ jsx(Label, { htmlFor: "phoneNumber", children: "Phone number" }),
                  /* @__PURE__ */ jsx(
                    FormikMaskedField,
                    {
                      name: "phoneNumber",
                      id: "phoneNumber",
                      inputMode: "decimal",
                      variant: "phone",
                      autoFocus: true,
                      fullWidth: true
                    }
                  ),
                  /* @__PURE__ */ jsx(ErrorMessage, { name: "phoneNumber", component: ErrorText })
                ] }),
                /* @__PURE__ */ jsx(Button, { disabled: isValidating, type: "submit", width: ["100%", "16.6rem"], children: "Continue" }),
                /* @__PURE__ */ jsx(Box, { mt: "5", children: /* @__PURE__ */ jsx(SecureInfoBadge, {}) })
              ] })
            }
          )
        ] }),
        /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "footer", children: /* @__PURE__ */ jsx(Footer, {}) })
      ]
    }
  );
};
export {
  FormikMaskedInput as F,
  PhoneNumber as P,
  SignupFlowHeader as S,
  FormikMaskedField as a
};
//# sourceMappingURL=PhoneNumber-CaezXveX.js.map
